import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import { Button } from 'components/Button'
import { Box, Flex, Grid } from 'components/Layout'
import { Text } from 'components/Typography'
import { HEADER_HEIGHT_MOBILE_FULL } from 'constants/common'
import { COLOR_INTENT, FONT_STACK, FONT_WEIGHT, Z_INDEX } from 'Theme'
import {
  ALL_TRADE_PERKS,
  DEFAULT_EXPLORER_TIER_INDEX,
  getCategoryFraction,
  getNewPerksAtTier,
  getUnlockedPerkCount,
  TRADE_PERK_CATEGORIES,
  TRADE_TIERS,
} from '../tradePerksData'
import { CheckCircleIcon, ChevronDownIcon, LockIcon } from './tradeIcons'

const { TRADE_PAGE } = COLOR_INTENT.WIDGETS

const TOTAL_PERK_COUNT = ALL_TRADE_PERKS.length
const LAST_TIER_INDEX = TRADE_TIERS.length - 1

const groupAnchorId = (key: string) => `trade-perk-group-${key}`

const getTabBackground = (isActive: boolean, isPlatinum: boolean) => {
  if (isActive) {
    return TRADE_PAGE.DARK_BG
  }
  return isPlatinum ? TRADE_PAGE.GOLD_TINT : TRADE_PAGE.WHITE
}

const getTabBorderColor = (isActive: boolean, isPlatinum: boolean) => {
  if (isActive) {
    return TRADE_PAGE.DARK_BG
  }
  return isPlatinum ? TRADE_PAGE.GOLD : TRADE_PAGE.BORDER_CARD
}

const getTabRangeColor = (isActive: boolean, isPlatinum: boolean) => {
  if (isActive) {
    return TRADE_PAGE.TEXT_ON_DARK_MUTED
  }
  return isPlatinum ? TRADE_PAGE.GOLD_DARK : TRADE_PAGE.EYEBROW
}

const StickyBar = styled(Box)`
  position: sticky;
  /* Mobile header is two 60px rows (HeaderWrapper uses MOBILE_FULL by
     default), so sticking at MOBILE_PARTIAL lands mid-header */
  top: ${HEADER_HEIGHT_MOBILE_FULL};
  z-index: ${Z_INDEX.STICKY_SUBNAV};
  background: ${TRADE_PAGE.SECTION_BG};
  border-top: 1px solid ${TRADE_PAGE.BORDER};
  border-bottom: 1px solid ${TRADE_PAGE.BORDER};
  padding: ${rem(10)} 0;
`

const TabGrid = styled(Grid)`
  grid-template-columns: repeat(5, 1fr);
  gap: ${rem(5)};
  padding: 0 ${rem(16)};
`

const TierTab = styled.button<{ $active: boolean; $isPlatinum: boolean }>`
  min-height: ${rem(48)};
  padding: ${rem(8)} ${rem(2)};
  border-radius: ${rem(8)};
  cursor: pointer;
  text-align: center;
  font: inherit;
  transition:
    background 0.15s,
    border-color 0.15s;
  background: ${({ $active, $isPlatinum }) =>
    getTabBackground($active, $isPlatinum)};
  border: 1px solid
    ${({ $active, $isPlatinum }) => getTabBorderColor($active, $isPlatinum)};
`

const ContentArea = styled(Box)`
  padding: ${rem(20)} ${rem(24)} 0;
`

const SummaryCard = styled(Box)`
  background: ${TRADE_PAGE.DARK_BG};
  border-radius: ${rem(14)};
  padding: ${rem(22)} ${rem(20)};
  box-shadow: 0 ${rem(10)} ${rem(30)} rgba(28, 53, 41, 0.22);
`

const MeterRow = styled(Flex)`
  gap: ${rem(4)};
  margin-bottom: ${rem(16)};
`

const MeterSegment = styled.span<{ $unlocked: boolean }>`
  flex: 1;
  height: ${rem(5)};
  border-radius: ${rem(3)};
  background: ${({ $unlocked }) =>
    $unlocked ? TRADE_PAGE.GOLD : 'rgba(255, 255, 255, 0.16)'};
`

const BucketGrid = styled(Grid)`
  grid-template-columns: 1fr 1fr;
  gap: ${rem(8)};
`

const BucketChip = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(6)};
  width: 100%;
  background: rgba(255, 255, 255, 0.07);
  border: none;
  border-radius: ${rem(6)};
  padding: ${rem(10)} ${rem(10)} ${rem(10)} ${rem(12)};
  cursor: pointer;
  min-height: ${rem(44)};
  text-align: left;
  font: inherit;
  color: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }
`

const IconSlot = styled.span`
  flex: 0 0 auto;
  display: flex;
  margin-top: ${rem(1)};
`

const LockRing = styled.span`
  flex: 0 0 auto;
  width: ${rem(20)};
  height: ${rem(20)};
  border-radius: 50%;
  border: 1px solid ${TRADE_PAGE.LOCKED_BG};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${rem(1)};
  color: ${TRADE_PAGE.LOCKED_ICON};
`

const NewPerkCard = styled(Flex)`
  background: ${TRADE_PAGE.WHITE};
  border: 1px solid ${TRADE_PAGE.BORDER_CARD};
  border-radius: ${rem(10)};
  padding: ${rem(14)} ${rem(16)};
  gap: ${rem(12)};
  align-items: flex-start;
`

const GroupHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(10)};
  width: 100%;
  border: none;
  border-bottom: 1px solid ${TRADE_PAGE.BORDER_SAND};
  background: none;
  padding: ${rem(10)} 0;
  cursor: pointer;
  min-height: ${rem(44)};
  text-align: left;
  font: inherit;
  color: inherit;
`

const GroupChevron = styled.span<{ $isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  color: ${TRADE_PAGE.CHEVRON};
  transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
  transition: transform 0.2s;
`

const PerkRow = styled(Flex)`
  gap: ${rem(12)};
  align-items: flex-start;
  padding: ${rem(12)} 0;
  border-bottom: 1px solid ${TRADE_PAGE.BORDER_ROW};
`

const UnlockChip = styled.button`
  font-size: ${rem(10)};
  letter-spacing: 0.06em;
  font-weight: ${FONT_WEIGHT.SEMI_BOLD};
  color: #8a7b64;
  background: ${TRADE_PAGE.TEXT_ON_DARK};
  border: 1px solid ${TRADE_PAGE.BORDER_CHIP};
  border-radius: ${rem(20)};
  padding: ${rem(3)} ${rem(9)};
  cursor: pointer;
  font-family: inherit;
`

const NudgeCard = styled(Box)`
  margin-top: ${rem(28)};
  background: ${TRADE_PAGE.MIST_BG};
  border: 1px solid ${TRADE_PAGE.MIST_BORDER};
  border-radius: ${rem(12)};
  padding: ${rem(20)};
`

const NudgeCta = styled(Button)`
  background: ${TRADE_PAGE.CTA_BG};
  color: ${TRADE_PAGE.CTA_TEXT};

  &:hover {
    background: ${TRADE_PAGE.CTA_BG_HOVER};
  }
`

const EndCard = styled(Box)`
  margin-top: ${rem(28)};
  background: ${TRADE_PAGE.DARK_BG};
  border-radius: ${rem(12)};
  padding: ${rem(20)};
  text-align: center;
`

export const TierExplorer: React.FC = () => {
  const [selectedTier, setSelectedTier] = React.useState(
    DEFAULT_EXPLORER_TIER_INDEX
  )
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(
    {}
  )

  const toggleGroup = (key: string) =>
    setOpenGroups((previous) => ({ ...previous, [key]: !previous[key] }))

  const jumpToGroup = (key: string) => {
    setOpenGroups((previous) => ({ ...previous, [key]: true }))
    requestAnimationFrame(() => {
      const element = document.getElementById(groupAnchorId(key))
      if (element) {
        const top =
          element.getBoundingClientRect().top + window.pageYOffset - 130
        window.scrollTo({ top, behavior: 'smooth' })
      }
    })
  }

  const currentTier = TRADE_TIERS[selectedTier]
  const unlockedCount = getUnlockedPerkCount(selectedTier)
  const newPerks = getNewPerksAtTier(selectedTier)
  const hasNext = selectedTier < LAST_TIER_INDEX
  const nextTier = hasNext ? TRADE_TIERS[selectedTier + 1] : null
  const gained = hasNext
    ? getUnlockedPerkCount(selectedTier + 1) - unlockedCount
    : 0

  return (
    <>
      <StickyBar>
        <TabGrid>
          {TRADE_TIERS.map((tier, index) => {
            const isActive = index === selectedTier
            const isPlatinum = index === LAST_TIER_INDEX

            return (
              <TierTab
                key={tier.name}
                type="button"
                $active={isActive}
                $isPlatinum={isPlatinum}
                onClick={() => setSelectedTier(index)}
              >
                <Text
                  as="div"
                  fontFamily={FONT_STACK.SERIF}
                  fontSize={rem(13)}
                  lineHeight="1.1"
                  color={
                    isActive ? TRADE_PAGE.TEXT_ON_DARK : TRADE_PAGE.DARK_BG
                  }
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {tier.name}
                </Text>
                <Text
                  as="div"
                  fontSize={rem(9)}
                  mt={rem(3)}
                  color={getTabRangeColor(isActive, isPlatinum)}
                  style={{ letterSpacing: '.02em', whiteSpace: 'nowrap' }}
                >
                  {tier.range}
                </Text>
              </TierTab>
            )
          })}
        </TabGrid>
      </StickyBar>

      <ContentArea>
        <SummaryCard>
          <Flex
            alignItems="baseline"
            justifyContent="space-between"
            gap={rem(12)}
          >
            <Text
              as="div"
              fontFamily={FONT_STACK.SERIF}
              fontSize={rem(30)}
              lineHeight="1"
              color={TRADE_PAGE.TEXT_ON_DARK}
            >
              {currentTier.name}
            </Text>
            <Text
              as="div"
              fontSize={rem(12)}
              fontWeight={FONT_WEIGHT.MEDIUM}
              color={TRADE_PAGE.TEXT_ON_DARK_MUTED}
              style={{ whiteSpace: 'nowrap' }}
            >
              {currentTier.range} annual spend
            </Text>
          </Flex>
          {currentTier.tagline && (
            <Text
              as="div"
              fontFamily={FONT_STACK.SERIF}
              fontStyle="italic"
              fontSize={rem(15)}
              color={TRADE_PAGE.GOLD}
              mt={rem(6)}
            >
              {currentTier.tagline}
            </Text>
          )}
          <Flex alignItems="center" gap={rem(10)} my={rem(16)}>
            <Text
              as="div"
              fontSize={rem(13)}
              fontWeight={FONT_WEIGHT.SEMI_BOLD}
              color={TRADE_PAGE.TABLE_HEADER_TEXT}
            >
              {unlockedCount} of {TOTAL_PERK_COUNT} perks unlocked
            </Text>
          </Flex>
          <MeterRow>
            {ALL_TRADE_PERKS.map((perk) => (
              <MeterSegment
                key={perk.name}
                $unlocked={selectedTier >= perk.fromTier}
              />
            ))}
          </MeterRow>
          <BucketGrid>
            {TRADE_PERK_CATEGORIES.map((category) => {
              const { unlocked, total } = getCategoryFraction(
                category.key,
                selectedTier
              )

              return (
                <BucketChip
                  key={category.key}
                  type="button"
                  onClick={() => jumpToGroup(category.key)}
                >
                  <Text
                    as="span"
                    fontSize={rem(11)}
                    fontWeight={FONT_WEIGHT.MEDIUM}
                    color={TRADE_PAGE.TEXT_ON_DARK_MUTED}
                    isEllipsis
                  >
                    {category.summaryLabel}
                  </Text>
                  <Flex alignItems="center" gap={rem(4)} flex="0 0 auto">
                    <Text
                      as="span"
                      fontSize={rem(12)}
                      fontWeight={FONT_WEIGHT.BOLD}
                      color={TRADE_PAGE.TEXT_ON_DARK}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {unlocked}/{total}
                    </Text>
                    <Box flex="0 0 auto" style={{ display: 'flex' }}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={TRADE_PAGE.TEXT_ON_DARK_MUTED}
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m9 6 6 6-6 6" />
                      </svg>
                    </Box>
                  </Flex>
                </BucketChip>
              )
            })}
          </BucketGrid>
        </SummaryCard>

        {newPerks.length > 0 && (
          <Box mt={rem(24)}>
            <Text
              as="div"
              fontSize={rem(11)}
              fontWeight={FONT_WEIGHT.BOLD}
              color={TRADE_PAGE.EYEBROW_ACCENT}
              mb={rem(12)}
              style={{ letterSpacing: '.18em', textTransform: 'uppercase' }}
            >
              New at {currentTier.name}
            </Text>
            <Flex flexDirection="column" gap={rem(10)}>
              {newPerks.map((perk) => (
                <NewPerkCard key={perk.name}>
                  <IconSlot>
                    <CheckCircleIcon size={20} />
                  </IconSlot>
                  <Box>
                    <Text
                      as="div"
                      fontSize={rem(13.5)}
                      fontWeight={FONT_WEIGHT.SEMI_BOLD}
                      color={TRADE_PAGE.TEXT_DARK}
                    >
                      {perk.name}
                    </Text>
                    <Text
                      as="div"
                      fontSize={rem(12.5)}
                      color={TRADE_PAGE.TEXT_SOFT}
                      lineHeight="1.5"
                      mt={rem(2)}
                    >
                      {perk.desc}
                    </Text>
                  </Box>
                </NewPerkCard>
              ))}
            </Flex>
          </Box>
        )}

        <Flex flexDirection="column" gap={rem(22)} mt={rem(28)}>
          {TRADE_PERK_CATEGORIES.map((category) => {
            const isOpen = Boolean(openGroups[category.key])
            const { unlocked, total } = getCategoryFraction(
              category.key,
              selectedTier
            )

            return (
              <Box key={category.key} id={groupAnchorId(category.key)}>
                <GroupHeader
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => toggleGroup(category.key)}
                >
                  <Text
                    as="span"
                    fontSize={rem(11)}
                    fontWeight={FONT_WEIGHT.BOLD}
                    color={TRADE_PAGE.ACCENT}
                    style={{
                      letterSpacing: '.16em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {category.titleLead} {category.titleEmphasis}
                  </Text>
                  <Flex alignItems="center" gap={rem(10)}>
                    <Text
                      as="span"
                      fontSize={rem(11)}
                      fontWeight={FONT_WEIGHT.SEMI_BOLD}
                      color={TRADE_PAGE.EYEBROW}
                    >
                      {unlocked} of {total}
                    </Text>
                    <GroupChevron $isOpen={isOpen}>
                      <ChevronDownIcon size={18} />
                    </GroupChevron>
                  </Flex>
                </GroupHeader>
                {isOpen &&
                  category.perks.map((perk) => {
                    const isUnlocked = selectedTier >= perk.fromTier

                    return (
                      <PerkRow key={perk.name}>
                        {isUnlocked ? (
                          <IconSlot>
                            <CheckCircleIcon size={20} />
                          </IconSlot>
                        ) : (
                          <LockRing>
                            <LockIcon size={10} />
                          </LockRing>
                        )}
                        <Box
                          flex="1"
                          color={
                            isUnlocked
                              ? TRADE_PAGE.TEXT_DARK
                              : TRADE_PAGE.TEXT_LOCKED
                          }
                          style={{ opacity: isUnlocked ? 1 : 0.75 }}
                        >
                          <Flex
                            alignItems="center"
                            gap={rem(8)}
                            flexWrap="wrap"
                          >
                            <Text
                              as="span"
                              fontSize={rem(13.5)}
                              fontWeight={FONT_WEIGHT.SEMI_BOLD}
                            >
                              {perk.name}
                            </Text>
                            {!isUnlocked && (
                              <UnlockChip
                                type="button"
                                onClick={() => setSelectedTier(perk.fromTier)}
                              >
                                Unlocks at {TRADE_TIERS[perk.fromTier].name}
                              </UnlockChip>
                            )}
                            {isUnlocked && perk.badge && (
                              <Text
                                as="span"
                                fontSize={rem(10.5)}
                                fontWeight={FONT_WEIGHT.MEDIUM}
                                color={TRADE_PAGE.HEADING}
                              >
                                {perk.badge}
                              </Text>
                            )}
                          </Flex>
                          <Text
                            as="div"
                            fontSize={rem(12.5)}
                            color={TRADE_PAGE.TEXT_SOFT}
                            lineHeight="1.5"
                            mt={rem(2)}
                          >
                            {perk.desc}
                          </Text>
                        </Box>
                      </PerkRow>
                    )
                  })}
              </Box>
            )
          })}
        </Flex>

        {hasNext && nextTier && (
          <NudgeCard>
            <Text
              as="div"
              fontSize={rem(11)}
              fontWeight={FONT_WEIGHT.BOLD}
              color={TRADE_PAGE.ACCENT}
              mb={rem(8)}
              style={{ letterSpacing: '.18em', textTransform: 'uppercase' }}
            >
              Keep leveling up
            </Text>
            <Text
              as="div"
              fontFamily={FONT_STACK.SERIF}
              fontSize={rem(20)}
              lineHeight="1.3"
              color={TRADE_PAGE.DARK_BG}
            >
              Move up to {nextTier.name} and unlock {gained} more perk
              {gained === 1 ? '' : 's'}
            </Text>
            <Text
              as="div"
              fontSize={rem(13)}
              color={TRADE_PAGE.TEXT_SUB}
              my={rem(8)}
              mb={rem(16)}
            >
              Reach {nextTier.range.replace('–', ' – ')} in annual spend across
              any of our brands
              {nextTier.tagline ? `. ${nextTier.tagline}.` : '.'}
            </Text>
            <NudgeCta
              width="auto"
              onClick={() =>
                setSelectedTier(Math.min(selectedTier + 1, LAST_TIER_INDEX))
              }
            >
              See {nextTier.name} perks
            </NudgeCta>
          </NudgeCard>
        )}

        {!hasNext && (
          <EndCard>
            <Text
              as="div"
              fontFamily={FONT_STACK.SERIF}
              fontStyle="italic"
              fontSize={rem(20)}
              color={TRADE_PAGE.GOLD}
            >
              Benefit like an Expert.
            </Text>
            <Text
              as="div"
              fontSize={rem(13)}
              color={TRADE_PAGE.TEXT_ON_DARK_MUTED}
              mt={rem(6)}
            >
              Platinum is the top of the program—every perk, unlocked.
            </Text>
          </EndCard>
        )}
      </ContentArea>
    </>
  )
}
